import type { CustomTypeOptions, TFunction } from "i18next";
import { BillingSupportNumber } from "../../components/UpfrontPayment/types";
import {
  AccordionProps,
  LINE_BREAK,
} from "../../components/shared/Accordion/Accordions";
import { CardProps } from "../../components/shared/Cards/Card";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { LandingFAQAnswerWrapper } from "./Landing.styles";
import geneticCounselor from "../../assets/images/geneticCounselor.png";
import { HelpFAQAnswerWrapper } from "../Help/Help.styles";
import BillingVideoEmbed from "../../components/shared/Video/BillingVideoEmbed";

type LandingFAQContentKey =
  keyof CustomTypeOptions["resources"]["translation"]["landing"]["faqContent"];

const translatedLandingFAQContent = (
  t: TFunction<"translation">,
  key: LandingFAQContentKey,
): React.ReactElement => (
  <LandingFAQAnswerWrapper>
    {GetTypographyContent({
      // FAQ answers are looked up by a dynamic key built at runtime, which the
      // typed t() key union can't verify statically. The value is always a real
      // `landing.faqContent.*` key; assert past the typed-key check.
      content: t(`landing.faqContent.${key}` as never),
      size: "medium",
    })}
  </LandingFAQAnswerWrapper>
);

export const landingPageAccordions = (
  reqID: string,
  t?: TFunction<"translation">,
): AccordionProps[] => {
  const allAccordions: AccordionProps[] = [
    {
      title: t
        ? t("landing.faq.unityAneuploidy")
        : "What does Unity Aneuploidy™ Screen test for?",
      content: t ? (
        translatedLandingFAQContent(t, "aneuploidyTestFor")
      ) : (
        <LandingFAQAnswerWrapper>
          {GetTypographyContent({
            content:
              "Unity Aneuploidy™ is a non-invasive prenatal test (NIPT) designed to identify certain chromosome changes that could affect your baby's health.",
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content:
              "Typically, humans have 23 pairs of chromosomes—46 in total—with one chromosome in each pair coming from each parent. Sometimes, a person can have an extra or missing chromosome, which is called aneuploidy. These chromosome changes can potentially lead to:",
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          <ul>
            <li>
              {GetTypographyContent({
                content: "Health conditions",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Physical birth defects",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Developmental delays",
                size: "medium",
              })}
            </li>
          </ul>
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content:
              "The type and severity of symptoms vary depending on which chromosome is extra or missing. Anyone can have a baby born with a chromosome change. Most aneuploidies does not run in families.",
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content:
              "Unity Aneuploidy Screen looks for the most common chromosome conditions present at birth:",
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          <ul>
            <li>
              {GetTypographyContent({
                content: "Trisomy 21 (Down syndrome)",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Trisomy 18 (Edwards syndrome)",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Trisomy 13 (Patau syndrome)",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "*Monosomy X (Turner syndrome)",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "*Other sex chromosome aneuploidies (XXX/XXY/XYY)",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "22q11.2 microdeletion syndrome",
                size: "medium",
              })}
            </li>
          </ul>
          {GetTypographyContent({
            content:
              "*Note, monosomy X and other sex chromosome aneuploidies are only screened for in singleton pregnancies.",
            size: "medium",
          })}
        </LandingFAQAnswerWrapper>
      ),
      key: "aneuploidyTestFor",
      padding: "3px 20px 3px 4px",
    },
    {
      title: t
        ? t("landing.faq.unityFetalRisk")
        : "What does Unity Fetal Risk™  Screen test for?",
      content: t ? (
        translatedLandingFAQContent(t, "carrierTestFor")
      ) : (
        <HelpFAQAnswerWrapper>
          {GetTypographyContent({
            content: `Carrier screening through Unity Fetal Risk Screen determines your carrier status for commonly inherited genetic conditions. Everyone has two copies of each gene, one inherited from each parent. Being a “carrier” means you have one working gene and one that doesn’t work perfectly. Carriers are typically not affected with the condition (not expected to have symptoms). If both you and your reproductive partner are carriers of the same condition, there is a 25% (1 in 4) chance to have an affected child with each pregnancy.`,
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content: `Most carriers of these conditions will not have symptoms or known family history of the condition. Therefore, medical guidelines recommend all pregnant individuals be given the option to screen for the following conditions covered by the Unity Fetal Risk Screen ACOG Guideline Panel:`,
            size: "medium",
          })}
          <ul>
            <li>
              {GetTypographyContent({
                content: "Alpha-thalassemia ",
                size: "medium",
              })}
              {GetTypographyContent({
                content: "(HBA1, HBA2)",
                size: "medium",
                isItalic: true,
              })}
            </li>
            <li>
              {GetTypographyContent({
                content:
                  "Sickle cell disease / beta-thalassemia / beta-hemoglobinopathy ",
                size: "medium",
              })}
              {GetTypographyContent({
                content: "(HBB)",
                size: "medium",
                isItalic: true,
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Cystic fibrosis ",
                size: "medium",
              })}
              {GetTypographyContent({
                content: "(CFTR)",
                size: "medium",
                isItalic: true,
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Spinal muscular atrophy ",
                size: "medium",
              })}
              {GetTypographyContent({
                content: "(SMN1)",
                size: "medium",
                isItalic: true,
              })}
            </li>
          </ul>
          {GetTypographyContent({
            content: `Most carriers of these conditions will not have symptoms or known family history of the condition. Therefore, medical guidelines recommend all pregnant individuals be given the option to screen for the following conditions covered by the Unity Fetal Risk Screen ACOG Guideline Panel:`,
            size: "medium",
          })}
          <ul>
            <li>
              {GetTypographyContent({
                content: "Canavan Disease ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Medium-Chain Acyl-CoA Dehydrogenase Deficiency ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Tay Sachs Disease  ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Familial Dysautonomia  ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Smith-Lemli-Opitz Syndrome ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "PMM2-Congenital Disorder of Glycosylation  ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "DMD-Associated Dystrophinopathies  ",
                size: "medium",
              })}
            </li>
            <li>
              {GetTypographyContent({
                content: "Phenylalanine Hydroxylase Deficiency (PKU)  ",
                size: "medium",
              })}
            </li>
          </ul>
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content: `If you are at least 9 weeks pregnant, and you are found to be a carrier, we will perform a fetal risk assessment automatically to evaluate your baby’s risk of having that condition.`,
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content: (
              <>
                <span style={{ fontWeight: "bold" }}>Note: </span> Cell-free DNA
                for recessive conditions cannot be performed in twins or higher
                order multiple pregnancies or pregnancies achieved via egg
                donors.
              </>
            ),
            size: "medium",
          })}
        </HelpFAQAnswerWrapper>
      ),
      key: "carrierTestFor",
    },
    {
      title: t ? (
        t("landing.faq.howToGetComplete")
      ) : (
        <>
          How can I get Unity Complete<sup>&reg;</sup>?
        </>
      ),
      content: (
        <LandingFAQAnswerWrapper>
          {GetTypographyContent({
            content: t
              ? t("landing.faqContent.howToGetTesting")
              : "Unity is a physician-ordered test. Please call us or email us at ",
            size: "medium",
          })}
          {!t && (
            <>
              {GetTypographyContent({
                content: "support@unityscreen.com",
                size: "medium",
                isEmail: true,
              })}
              {GetTypographyContent({
                content:
                  " so we can help you get the testing done through your OB or midwife.",
                size: "medium",
              })}
            </>
          )}
        </LandingFAQAnswerWrapper>
      ),
      key: "howToGetTesting",
      padding: "3px 20px 3px 4px",
    },
    {
      title: t
        ? t("landing.faq.billingAndInsurance")
        : "Billing and insurance process overview",
      content: (
        <LandingFAQAnswerWrapper style={{ display: "grid" }}>
          <BillingVideoEmbed
            reqID={reqID}
            videoLocation="dashboard-faq-section"
          />
        </LandingFAQAnswerWrapper>
      ),
      key: "billingInsuranceVideo",
      padding: "3px 20px 3px 4px",
    },
    {
      title: t ? (
        t("landing.faq.unityCompleteCovered")
      ) : (
        <>
          Is Unity Complete<sup>&reg;</sup> covered by my insurance?
        </>
      ),
      content: t ? (
        translatedLandingFAQContent(t, "isCovered")
      ) : (
        <LandingFAQAnswerWrapper>
          {GetTypographyContent({
            content: (
              <>
                We accept all insurances including Medicaid. Medical guidelines
                recommend every pregnant woman to be offered screening for the
                conditions covered on Unity Complete<sup>&reg;</sup>; therefore,
                most major private insurance plans and Medicaid plans cover the
                testing. Depending on your insurance details, there may be a
                co-payment or deductibles you are responsible for.
              </>
            ),
            size: "medium",
          })}

          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content: `If you have any questions or concerns with testing cost, please email us at `,
            size: "medium",
          })}
          {GetTypographyContent({
            content: "support@unityscreen.com",
            size: "medium",
            isEmail: true,
          })}
          {GetTypographyContent({
            content: " or call us at ",
            size: "medium",
          })}
          {GetTypographyContent({
            content: BillingSupportNumber,
            size: "medium",
            isPhone: true,
          })}
        </LandingFAQAnswerWrapper>
      ),
      key: "isCovered",
      padding: "3px 20px 3px 4px",
    },
    {
      title: t
        ? t("landing.faq.moreQuestions")
        : "I have more questions. Can I talk to the genetic counselor before taking the test?",
      content: t ? (
        translatedLandingFAQContent(t, "moreQuestions")
      ) : (
        <LandingFAQAnswerWrapper>
          {GetTypographyContent({
            content:
              "Yes, we provide pre-test and post-test genetic counseling at no charge. To schedule an appointment, please go to our ",
            size: "medium",
          })}
          {GetTypographyContent({
            content: "scheduling page.",
            size: "medium",
            link: "./client/scheduling",
            textColor: "greenMedium",
          })}
        </LandingFAQAnswerWrapper>
      ),
      key: "moreQuestions",
      padding: "3px 20px 3px 4px",
    },
    {
      title: t
        ? t("landing.faq.eob")
        : "What is an Explanation of Benefits (EOB)?",
      content: t ? (
        translatedLandingFAQContent(t, "eob")
      ) : (
        <HelpFAQAnswerWrapper>
          {GetTypographyContent({
            content: `After your test has been processed and a claim is submitted to your insurance, 
            you may receive an Explanation of Benefits (EOB). `,
            size: "medium",
          })}
          {GetTypographyContent({
            content: `An EOB is not a bill`,
            size: "medium",
            isBold: true,
          })}
          {GetTypographyContent({
            content: `, even though it may look like one. This document is from your insurance and shows details like how much 
            was billed to your insurance for your test; what the insurance reimbursed BillionToOne; and the
             specifics of your insurance plan (deductible), if applicable.`,
            size: "medium",
          })}
          {LINE_BREAK}
          {LINE_BREAK}
          {GetTypographyContent({
            content: `There is no action you need to take on an EOB. BillionToOne will send a final bill with any remaining balance after you receive an EOB.`,
            size: "medium",
          })}
        </HelpFAQAnswerWrapper>
      ),
      key: "explainWOB",
    },
  ];

  return allAccordions;
};

export const landingPageCard = (
  deviceType: DeviceType,
  t?: TFunction<"translation">,
): CardProps => ({
  buttons: [],
  component: "img",
  titleTypography: {
    content: t
      ? t("landing.gcCard.title")
      : "Schedule a complimentary genetic counseling session.",
    isBold: true,
    isCaps: false,
    size: "h2",
    padding: `${
      deviceType === "desktop" ? "10px 30px 20px 0px" : "10px 0 15px 0"
    }`,
  },
  bodyTypography: {
    content: t
      ? t("landing.gcCard.body")
      : "Our board certified and licensed genetic counselors are available to answer your questions and help you understand your results",
    isBold: false,
    isCaps: false,
    size: "medium",
    padding: `${
      deviceType === "desktop" ? "10px 30px 60px 0px" : "0 0 30px 0"
    }`,
  },
  gridDimension: "4fr 6fr",
  src: geneticCounselor,
  buttonSize: "medium",
});

export const landingPageGCLearnMoreCard = (
  deviceType: DeviceType,
  t?: TFunction<"translation">,
): CardProps => ({
  buttons: [],
  component: "img",
  titleTypography: {
    content: t
      ? t("landing.gcCard.learnMoreTitle")
      : "Learn more about genetic counseling sessions.",
    isBold: true,
    isCaps: false,
    size: "h2",
    padding: `${
      deviceType === "desktop" ? "10px 30px 20px 0px" : "10px 0 15px 0"
    }`,
  },
  bodyTypography: {
    content: t
      ? t("landing.gcCard.learnMoreBody")
      : "Once your results are ready, our board certified and licensed genetic counselors are available to help you understand them and to answer your questions.",
    isBold: false,
    isCaps: false,
    size: "medium",
    padding: `${
      deviceType === "desktop" ? "10px 30px 60px 0px" : "0 0 30px 0"
    }`,
  },
  gridDimension: "4fr 6fr",
  src: geneticCounselor,
  buttonSize: "medium",
});
